import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { hierarchyService } from '../../services/hierarchyService';
import { userService } from '../../services/userService';
import { cn } from '../../utils/cn';
import HierarchyEntityTable from './HierarchyEntityTable';
import ActionToast from '../../components/ui/ActionToast';

const Department = () => {
    const [departments, setDepartments] = useState([]);
    const [faculties, setFaculties] = useState([]);
    const [hods, setHods] = useState([]);

    // Filter State
    const [selectedFacultyId, setSelectedFacultyId] = useState('all');

    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [notice, setNotice] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        faculty_id: '',
        hod_id: '',
        description: ''
    });

    // Loading states for dropdowns
    const [loadingFaculties, setLoadingFaculties] = useState(false);
    const [loadingHods, setLoadingHods] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await hierarchyService.getDepartments();
            setDepartments(data || []);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdowns = async () => {
        // Fetch Faculties
        try {
            setLoadingFaculties(true);
            const facData = await hierarchyService.getFaculties();
            setFaculties(facData.map(f => ({ label: f.name, value: f.id, original: f })));
        } catch (e) { console.error(e); } finally { setLoadingFaculties(false); }

        // Fetch HODs
        try {
            setLoadingHods(true);
            const userData = await userService.getUsers({ role: 'head_of_department' });
            setHods(userData.map(u => ({ label: `${u.full_name} (${u.email})`, value: u.id })));
        } catch (e) { console.error(e); } finally { setLoadingHods(false); }
    };

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, []);

    const clearForm = () => {
        setFormData({ name: '', code: '', faculty_id: '', hod_id: '', description: '' });
        setIsEditing(false);
        setEditId(null);
    };

    const handleEdit = (dept) => {
        setFormData({
            name: dept.name,
            code: dept.code,
            faculty_id: dept.faculty_id || '',
            hod_id: dept.hod_id || '',
            description: dept.description || ''
        });
        setEditId(dept.id);
        setIsEditing(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this department? This might affect Sections linked to it.')) {
            try {
                await hierarchyService.deleteDepartment(id);
                fetchData();
            } catch (error) {
                console.error('Error deleting department:', error);
                alert('Failed to delete department. It may have related records.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await hierarchyService.updateDepartment(editId, formData);
                setNotice({ type: 'success', message: `${formData.name} was updated successfully.` });
            } else {
                await hierarchyService.createDepartment(formData);
                setNotice({ type: 'success', message: `${formData.name} was created successfully.` });
            }
            clearForm();
            setShowForm(false);
            fetchData();
        } catch (error) {
            console.error('Error saving department:', error);
            setNotice({ type: 'error', message: error.message || 'The department could not be saved.' });
        }
    };

    // Filter Logic
    const filteredDepartments = selectedFacultyId === 'all'
        ? departments
        : departments.filter(d => d.faculty_id === selectedFacultyId);

    // Get unique faculties from fetched faculties list for the filter bar
    // We use the `faculties` state which stores options, but we kept `original` in options mapping for this purpose if needed,
    // or just use the options directly since they have label/value
    const facultyOptions = faculties;

    return (
        <AdminLayout title="Department Management">
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            <div className="space-y-6">
                {/* Single-line Minimalistic Toolbar */}
                <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h1 className="text-lg font-bold tracking-tight text-slate-900">Departments</h1>
                        <span className="text-xs text-slate-500">· {filteredDepartments.length} total</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
                        <select
                            value={selectedFacultyId}
                            onChange={(e) => setSelectedFacultyId(e.target.value)}
                            className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 sm:w-auto sm:max-w-[280px]"
                        >
                            <option value="all">All Departments</option>
                            {facultyOptions.map(faculty => (
                                <option key={faculty.value} value={faculty.value}>{faculty.label}</option>
                            ))}
                        </select>

                        <Button
                            size="sm"
                            className="h-8 text-xs px-3 font-semibold shrink-0"
                            onClick={() => {
                                if (showForm && !isEditing) {
                                    clearForm();
                                    setShowForm(false);
                                } else {
                                    clearForm();
                                    setShowForm(true);
                                }
                            }}
                        >
                            <Icon name={showForm && !isEditing ? "X" : "Plus"} className="mr-1.5 h-3.5 w-3.5" />
                            {showForm && !isEditing ? 'Cancel' : 'Add Department'}
                        </Button>
                    </div>
                </div>

                {showForm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
                        <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl space-y-4">
                            <div className="flex items-center justify-between border-b pb-3">
                                <h2 className="text-base font-bold text-slate-900">{isEditing ? 'Edit Department' : 'Create New Department'}</h2>
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:bg-slate-100 rounded-lg" onClick={() => { clearForm(); setShowForm(false); }}>
                                    <Icon name="X" className="h-4 w-4" />
                                </Button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Input
                                        label="Department Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="e.g. Computer Science"
                                    />
                                    <Input
                                        label="Department Code"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        required
                                        placeholder="e.g. CS"
                                    />
                                    <Select
                                        label="Faculty"
                                        options={faculties}
                                        value={formData.faculty_id}
                                        onChange={(val) => setFormData({ ...formData, faculty_id: val })}
                                        loading={loadingFaculties}
                                        placeholder="Select Faculty..."
                                        searchable
                                        required
                                    />
                                    <div className="space-y-1">
                                        <Select
                                            label="Head of Department"
                                            options={hods}
                                            value={formData.hod_id}
                                            onChange={(val) => setFormData({ ...formData, hod_id: val })}
                                            loading={loadingHods}
                                            placeholder="Select HOD..."
                                            searchable
                                            required
                                        />
                                        <div className="text-xs text-slate-500 text-right">
                                            Can't find person?{' '}
                                            <NavLink to="/enhanced-user-management-system" className="text-slate-900 font-semibold hover:underline">
                                                Assign 'HOD' Role
                                            </NavLink>
                                        </div>
                                    </div>
                                    <Input
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Brief description..."
                                        className="md:col-span-2"
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-3 border-t">
                                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => { clearForm(); setShowForm(false); }}>Cancel</Button>
                                    <Button type="submit" size="sm" className="h-8 text-xs font-semibold">{isEditing ? 'Update Department' : 'Create Department'}</Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-10">Loading departments...</div>
                ) : (
                    <HierarchyEntityTable type="department" entities={filteredDepartments} onEdit={handleEdit} onDelete={handleDelete} emptyMessage={selectedFacultyId === 'all' ? 'No departments found. Click "Add Department" to create one.' : 'No departments found in this faculty.'} />
                )}
            </div>
        </AdminLayout>
    );
};

export default Department;
